<template>
  <div class="relative aspect-video" :class="{ [dimensions.classes]: size }">
    <iframe
      v-if="showIframe"
      allow="autoplay; encrypted-media"
      allowfullscreen
      class="aspect-video w-full"
      data-test="youtube-iframe"
      frameborder="0"
      loading="lazy"
      :src="`https://www.youtube.com/embed/${youtubeId}?autoplay=1&playsinline=1&rel=0`"
      :title="title || undefined"
    />

    <template v-else>
      <ImageRenderer
        :alt="title || ''"
        class="absolute inset-0 h-full w-full"
        :height="dimensions.height"
        imageClass="h-full object-cover"
        :src="thumbnailSrc"
        :src-mobile="thumbnailSrcMobile"
        :width="dimensions.width"
      />

      <RevIllustration
        alt=""
        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        :height="48"
        src="/img/cms/YoutubePlay.svg"
        :width="68"
      />

      <RevButtonBase
        v-if="showFacade"
        class="absolute inset-0"
        data-test="youtube-facade"
        @click="onPlaceholderClick"
      />

      <template v-else-if="showConsentPlaceholder">
        <RevBackdrop
          background-color="hi"
          class="absolute inset-0 flex flex-col items-center justify-center gap-16 p-16 text-center mood-inverse"
          data-test="youtube-consent-placeholder"
        >
          <p class="body-1 text-static-default-hi">
            {{ i18n(translations.description) }}
          </p>

          <RevButton variant="primary" @click="onAcceptClick">
            {{ i18n(translations.button) }}
          </RevButton>
        </RevBackdrop>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, onMounted, ref, watch } from 'vue'

import type { Image } from '@backmarket/http-api/src/api-specs-content/models/image'
import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import type { EmbeddedYoutubeVideoApiProps } from '@backmarket/http-api/src/api-specs-content/models/video'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { tw } from '@backmarket/utils/string/tw'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { RevBackdrop } from '@ds/components/Backdrop'
import { RevButton } from '@ds/components/Button'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'

import ImageRenderer from '../ImageRenderer/ImageRenderer.vue'

import translations from './YoutubeEmbedded.translations'

type YoutubeEmbeddedProps = {
  youtubeId: string
  title?: Maybe<string>
  size?: EmbeddedYoutubeVideoApiProps['size']
  image?: Maybe<Image>
  tracking?: Tracking
}

const props = withDefaults(defineProps<YoutubeEmbeddedProps>(), {
  title: '',
  placeholderAttrs: undefined,
  size: undefined,
  image: undefined,
  tracking: undefined,
})

const { trackClick } = useTracking()
const i18n = useI18n()
const {
  isDidomiEnabled,
  isDidomiReady,
  isYoutubeUserConsentGiven,
  acceptYoutubeVendor,
} = useDataConsent()
const { FF_YOUTUBE_CONSENT_GATE_ENABLED } = useRuntimeConfig().public
const isYoutubeConsentGateEnabled = FF_YOUTUBE_CONSENT_GATE_ENABLED === true

const userClickedPlay = ref<boolean>(true)

// Avoid hydration mismatch: SSR always renders the facade because isDidomiReady is false on the server.
// On the client, Didomi may already beready and consent missing at hydration time, which would render the consent placeholder instead.
const mounted = ref(false)

onMounted(() => {
  mounted.value = true
})

const showConsentPlaceholder = computed(
  () =>
    mounted.value &&
    isDidomiEnabled &&
    isDidomiReady.value &&
    !isYoutubeUserConsentGiven.value &&
    isYoutubeConsentGateEnabled,
)
const showFacade = computed(
  () => !showConsentPlaceholder.value && userClickedPlay.value,
)
const showIframe = computed(
  () => !showConsentPlaceholder.value && !userClickedPlay.value,
)

watch(isYoutubeUserConsentGiven, (given, prev) => {
  if (prev && !given) {
    userClickedPlay.value = true
  }
})

const dimensions = computed(() => {
  if (props.size === 'medium') {
    return {
      height: 630,
      width: 1120,
      classes: tw`w-[1120px]`,
    }
  }

  return {
    height: 415,
    width: 736,
    classes: tw`w-[736px]`,
  }
})

const thumbnailSrc = computed(
  () =>
    props.image?.src ??
    `https://img.youtube.com/vi/${props.youtubeId}/hqdefault.jpg`,
)
const thumbnailSrcMobile = computed(
  () =>
    props.image?.srcMobile ??
    `https://img.youtube.com/vi/${props.youtubeId}/hqdefault.jpg`,
)

function trackVideoClick() {
  trackClick(
    removeEmptyValuesInObject({
      ...props.tracking,
      blockTitle: props.title || '',
    }),
  )
}

function onPlaceholderClick() {
  userClickedPlay.value = false
  trackVideoClick()
}

function onAcceptClick() {
  acceptYoutubeVendor()
  trackVideoClick()
  userClickedPlay.value = false
}
</script>
